import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Clock, Lock } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type GearListStatus = 'still_prepping' | 'locked';

interface GearListStatusIndicatorProps {
  projectId: string;
  status: GearListStatus;
  canEdit?: boolean;
}

const statusConfig = {
  still_prepping: {
    label: "Still Prepping",
    icon: Clock,
    color: "bg-amber-500 text-white border-amber-500/20",
  },
  locked: {
    label: "Locked",
    icon: Lock,
    color: "bg-red-500 text-white border-red-500/20",
  },
};

export function GearListStatusIndicator({ 
  projectId, 
  status, 
  canEdit = true 
}: GearListStatusIndicatorProps) {
  const [isChanging, setIsChanging] = useState(false);
  const queryClient = useQueryClient();
  
  const config = statusConfig[status];
  const Icon = config.icon;

  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: GearListStatus) => {
      const { error } = await supabase
        .from('projects')
        .update({ gear_list_status: newStatus })
        .eq('id', projectId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      toast.success("Gear list status updated");
      setIsChanging(false);
    },
    onError: (error) => {
      console.error('Error updating status:', error);
      toast.error("Failed to update status");
      setIsChanging(false);
    },
  });

  const handleStatusChange = (newStatus: GearListStatus) => {
    if (newStatus === status) return;
    setIsChanging(true);
    updateStatusMutation.mutate(newStatus);
  };

  if (!canEdit) {
    return (
      <Badge className={`gap-1.5 ${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`gap-1.5 h-auto px-3 py-1.5 ${config.color}`}
          disabled={isChanging}
        >
          <Icon className="h-3 w-3" />
          {config.label}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {Object.entries(statusConfig).map(([key, statusData]) => {
          const StatusIcon = statusData.icon;
          return (
            <DropdownMenuItem
              key={key}
              onClick={() => handleStatusChange(key as GearListStatus)}
              className="gap-2"
            >
              <StatusIcon className="h-4 w-4" />
              {statusData.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}